import { authGet, authPost, authPut } from '../../utils/authFetch';
import { wmServerAddress } from '../../common/MetaSetting';
import { fetchHttpResponseHandler, fetchHttpExceptionHandler } from '../../util/UtilFunction';
import { HttpResponseType } from '../../model/AppUIShapes';
import { CLIENT_ID } from '../../util/GUID';
import { ApiMemo, ApiShare, ApiShareSyncRequest, ApiMemoSyncRequest, ApiShareStatus } from '../../model/ApiShapes';
import { UIMemo } from '../../model/UiShapes';
import { apiMemoToUIMemo, uiMemoToApiMemo, apiMemoContentToUIMemoContent } from '../../model/DataConverters';
import { handleError } from '../managers/errorHandler';

export function fetchMemos(): Promise<UIMemo[]> {
  return authGet(`${wmServerAddress}/memo/all`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .then((response) => {
      if (response?.responseType === HttpResponseType.SUCCESS && response?.content) {
        return (response.content || []).map((apiMemo: ApiMemo) => apiMemoToUIMemo(apiMemo));
      }
      // 请求失败时抛错，让 React Query 的 retry/refetchOnReconnect 生效，
      // 不再吞成空数组污染缓存（弱网下显示"空工作区"而非错误态）
      throw new Error(`fetchMemos failed: ${response?.message || 'unknown error'}`);
    });
}

export function syncMemo(memo: UIMemo): Promise<{syncResult: number}> {
  const apiMemo = uiMemoToApiMemo({...memo, modifyTimestamp: Date.now()});

  const apiMemoSyncRequest: ApiMemoSyncRequest = {
    clientId: CLIENT_ID,
    syncType: 0,
    GUID: memo.guid,
    sourceMemo: apiMemo
  };

  // 异常直接抛给调用方（updateMemoAsync 已有回滚 NOTUPDATE + handleError 逻辑），
  // 不再把网络错误吞成 syncResult:0（无更新）或把缺字段/HTTP 错误响应吞成 syncResult:1（假成功）
  return authPut(`${wmServerAddress}/memo/sync`, {
    body: JSON.stringify(apiMemoSyncRequest)
  })
    .then((response: any) => fetchHttpResponseHandler(response))
    .then((response) => {
      if (
        response?.responseType === HttpResponseType.SUCCESS &&
        response?.content &&
        typeof response.content.syncResult === 'number'
      ) {
        return {
          syncResult: response.content.syncResult as number
        };
      }
      throw new Error(`invalid sync response for memo ${memo.guid}`);
    });
}

export function fetchMemo(memoId: string): Promise<{ content: UIMemo } | null> {
  handleError(new Error(`fetchMemo: 开始获取memo ${memoId}`), { context: 'memoAPI', showToast: false });
  return authGet(`${wmServerAddress}/memo/${memoId}`)
    .then((response: any) => fetchHttpResponseHandler(response))
    .then((response) => {
      if (response?.responseType === HttpResponseType.SUCCESS && response?.content) {
        const uiMemo = apiMemoToUIMemo(response.content as ApiMemo);
        return { content: uiMemo };
      }
      // 网络错误/HTTP 错误抛给调用方（如 syncMemo 冲突回源），
      // 只有请求成功但无内容时才返回 null
      if (response?.responseType === HttpResponseType.SUCCESS) {
        return null;
      }
      throw new Error(`fetchMemo failed: ${response?.message || 'unknown error'}`);
    });
}

export function triggerAnalysis(request: any): Promise<any> {
  return authPost(`${wmServerAddress}/analysis`, {
    body: JSON.stringify(request)
  })
    .then((response: any) => fetchHttpResponseHandler(response))
    .catch(fetchHttpExceptionHandler);
}

// Query keys
export const memoKeys = {
  all: ['memos'] as const,
  lists: () => [...memoKeys.all, 'list'] as const,
  list: () => [...memoKeys.lists(), {}] as const,
  memos: () => [...memoKeys.all, 'memo'] as const,
  memo: (id: string) => [...memoKeys.memos(), id] as const,
};